'use client';

import { useRouter, usePathname } from 'next/navigation';
import { useState, type MouseEvent } from 'react';
import { Collapse } from '@mui/material';
import { UserRole } from '@/lib/types';
import { DocumentTypeCode } from '@/apiClient';
import { usePrivate } from '@/components/private/PrivateContext';
import { useLogout } from '@/hooks/useLogout';
import { useIsAbriStaff } from '@/hooks/useIsAbriStaff';
import { MenuIcon } from '@/primitives/icons';
import {
  Box,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@/primitives';
import { DocumentAvatar } from '@/components/avatars/DocumentAvatar';
import { userAvatarMessages } from './messages';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import AppRegistrationOutlinedIcon from '@mui/icons-material/AppRegistrationOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { PUBLIC_ROUTES } from '@/configs/routes';

export interface UserAvatarProps {
  /** When true, uses smaller avatar, text, and menu icon (e.g. for society header). */
  compact?: boolean;
  /** When true, renders menu items as an inline collapsible instead of a popover. */
  inline?: boolean;
}

const menuItemSx = {
  py: '6px',
  px: '12px',
  minHeight: 0,
  gap: '8px',
  fontSize: '13px',
  '& .MuiListItemIcon-root': {
    minWidth: 0,
    color: 'text.secondary',
  },
} as const;

const inlineItemSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  px: '4px',
  py: '6px',
  fontSize: '13px',
  color: 'text.secondary',
  cursor: 'pointer',
  borderRadius: '4px',
  '&:hover': { backgroundColor: 'action.hover' },
} as const;

export function UserAvatar({
  compact = false,
  inline = false,
}: UserAvatarProps) {
  const router = useRouter();
  const pathname = usePathname();
  const isOnManageUsersPage = pathname.endsWith('/account/manage-users');
  const isOnAccountPage =
    pathname.startsWith('/account') && !isOnManageUsersPage;
  const isAbriStaff = useIsAbriStaff();
  const { userProfile, userOrganisations, selectedOrganisationId } =
    usePrivate();
  const { logout } = useLogout();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expanded, setExpanded] = useState<boolean>(false);

  const open = Boolean(anchorEl);

  const userOrganisationsFound = userOrganisations?.find(
    (o) => o.organisationId === selectedOrganisationId,
  );

  const handleClickUserProfileButton = (event: MouseEvent<HTMLDivElement>) => {
    if (inline) {
      setExpanded((prev) => !prev);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleClickAccount = () => {
    setAnchorEl(null);
    setExpanded(false);
    router.push('/account');
  };

  const handleClickRegistry = () => {
    setAnchorEl(null);
    router.push(PUBLIC_ROUTES.REGISTRY_LANDING);
  };

  const handleClickManageUsers = () => {
    setAnchorEl(null);
    setExpanded(false);
    router.push('/account/manage-users');
  };

  const handleClickLogout = () => {
    setAnchorEl(null);
    setExpanded(false);
    logout();
  };

  const getInitials = () => {
    if (userProfile?.firstName) {
      return (
        (userProfile.firstName?.at(0) || '') +
        (userProfile.lastName?.at(0) || '')
      );
    }
    if (userProfile?.preferredName) {
      return userProfile.preferredName.at(0) || '';
    }
    return '';
  };

  const getName = () => {
    if (userProfile?.firstName) {
      return (
        userProfile.firstName +
        (userProfile.lastName ? ` ${userProfile.lastName}` : '')
      );
    }
    return userProfile?.preferredName || userProfile?.displayName;
  };

  const trigger = (
    <Box
      aria-controls={!inline && open ? 'user-profile-menu' : undefined}
      aria-haspopup={!inline ? 'true' : undefined}
      aria-expanded={inline ? expanded : open ? 'true' : undefined}
      id="user-profile-button"
      onClick={handleClickUserProfileButton}
      sx={{
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        width: inline ? '100%' : undefined,
      }}
    >
      <DocumentAvatar
        documentTypeCode={DocumentTypeCode.Avatar}
        documentEntityId={userProfile?.userProfileEntityId}
        sx={{
          bgcolor: 'primary.main',
          color: 'common.white',
          ...(compact ? { width: 28, height: 28, fontSize: '0.75rem' } : {}),
        }}
      >
        {getInitials()}
      </DocumentAvatar>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingLeft: compact ? '8px' : '12px',
        }}
      >
        <Typography
          variant={compact ? 'bodysmall' : 'titlemedium'}
          sx={{ color: 'text.primary' }}
        >
          {getName()}
        </Typography>
        {userOrganisationsFound && !compact && (
          <Typography variant="bodysmall" color="text.secondary">
            {UserRole[userOrganisationsFound.roleId!]}
          </Typography>
        )}
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      {inline ? (
        <ExpandMoreIcon
          sx={{
            fontSize: '22px',
            color: 'text.primary',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      ) : (
        <MenuIcon
          sx={{
            fontSize: compact ? '24px' : '48px',
            color: 'text.primary',
          }}
        />
      )}
    </Box>
  );

  if (inline) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {trigger}
        <Collapse in={expanded} unmountOnExit>
          <Box sx={{ pt: 0.5, pb: 0.25 }}>
            <Box
              sx={{
                ...inlineItemSx,
                color: isOnAccountPage ? 'primary.main' : 'text.secondary',
              }}
              onClick={handleClickAccount}
            >
              <PersonOutlinedIcon
                sx={{
                  fontSize: 18,
                  color: isOnAccountPage ? 'primary.main' : 'text.secondary',
                }}
              />
              {userAvatarMessages['userAvatar.menu.account.label']}
            </Box>
            {isAbriStaff ? (
              <Box
                sx={{
                  ...inlineItemSx,
                  color: isOnManageUsersPage
                    ? 'primary.main'
                    : 'text.secondary',
                }}
                onClick={handleClickManageUsers}
              >
                <ManageAccountsIcon
                  sx={{
                    fontSize: 18,
                    color: isOnManageUsersPage
                      ? 'primary.main'
                      : 'text.secondary',
                  }}
                />
                {userAvatarMessages['userAvatar.menu.manageUsers.label']}
              </Box>
            ) : null}
            <Divider sx={{ my: '4px' }} />
            <Box sx={inlineItemSx} onClick={handleClickLogout}>
              <LogoutOutlinedIcon
                sx={{ fontSize: 18, color: 'text.secondary' }}
              />
              {userAvatarMessages['userAvatar.menu.logout.label']}
            </Box>
          </Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <>
      {trigger}
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        id="user-profile-menu"
        onClose={handleCloseMenu}
        autoFocus={false}
        open={open}
        slotProps={{
          list: {
            'aria-labelledby': 'user-profile-button',
            autoFocusItem: false,
            sx: { py: '4px' },
          },
          paper: {
            sx: { minWidth: 180, borderRadius: '8px', mt: '4px' },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <MenuItem
          sx={{
            ...menuItemSx,
            color: isOnAccountPage ? 'primary.main' : undefined,
            '& .MuiListItemIcon-root': {
              minWidth: 0,
              color: isOnAccountPage ? 'primary.main' : 'text.secondary',
            },
          }}
          selected={isOnAccountPage}
          onClick={handleClickAccount}
        >
          <ListItemIcon>
            <PersonOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {userAvatarMessages['userAvatar.menu.account.label']}
        </MenuItem>
        {isAbriStaff ? (
          <MenuItem
            sx={{
              ...menuItemSx,
              color: isOnManageUsersPage ? 'primary.main' : undefined,
              '& .MuiListItemIcon-root': {
                minWidth: 0,
                color: isOnManageUsersPage ? 'primary.main' : 'text.secondary',
              },
            }}
            selected={isOnManageUsersPage}
            onClick={handleClickManageUsers}
          >
            <ListItemIcon>
              <ManageAccountsIcon fontSize="small" />
            </ListItemIcon>
            {userAvatarMessages['userAvatar.menu.manageUsers.label']}
          </MenuItem>
        ) : null}
        <MenuItem sx={menuItemSx} onClick={handleClickRegistry}>
          <ListItemIcon>
            <AppRegistrationOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {userAvatarMessages['userAvatar.menu.registry.label']}
        </MenuItem>
        <Divider sx={{ my: '4px' }} />
        <MenuItem sx={menuItemSx} onClick={handleClickLogout}>
          <ListItemIcon>
            <LogoutOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {userAvatarMessages['userAvatar.menu.logout.label']}
        </MenuItem>
      </Menu>
    </>
  );
}
